import { useMemo } from 'react'
import { KpiTile } from '../../charts/KpiTile'
import { WidgetFrame, type WidgetLoadState } from '../../widgets/WidgetFrame'
import { listAiReviews } from '../../../../api/ai'
import { useBoundedList, PAGE_LIMIT } from './useBoundedList'
import { computeAcceptRate } from './analyticsMappers'
import type { AiReviewView } from '../../../../types/ai'

export interface AiAcceptRateWidgetProps {
  /**
   * The manifest's `ai-accept-rate` capability render-gate (design §3.1 — manifest-gate-only).
   * `null` while the manifest fetch is still in flight — renders `loading`, never a premature
   * `not_authorized` flash.
   */
  available: boolean | null
  refreshKey: string
}

/**
 * IB-282 View 4, AC4.2 — "AI-accept rate". Manifest-gate-only (design §3.1): there is no
 * `/insights/widgets/ai-accept-rate` endpoint — this tile walks the (now `ai_review:R`-gated)
 * `GET /api/v1/ai-reviews` client-side (bounded, {@link useBoundedList}) and computes {@code
 * accepted / (accepted + rejected)}, EXCLUDING {@code decision="edit"} from both halves of the ratio
 * (design §2 — the DoR's literal formula) and disclosing it separately as an "edited" count. A zero
 * denominator renders "No activity in this period", never a fabricated `0%` that would falsely
 * assert rejections happened (AC4.2's own failure mode).
 */
export function AiAcceptRateWidget({ available, refreshKey }: AiAcceptRateWidgetProps) {
  const { status, rows, overflow, message, retry } = useBoundedList<AiReviewView>(
    available === true,
    (cursor, signal) => listAiReviews({ cursor, limit: PAGE_LIMIT }, signal),
    refreshKey,
  )

  const rate = useMemo(() => computeAcceptRate(rows), [rows])
  const hasActivity = rate.ratePct !== null
  const frameState: WidgetLoadState =
    available === null ? 'loading' : overflow ? 'empty' : status === 'ok' && !hasActivity ? 'empty' : status
  // The "No activity"/overflow copy applies ONLY to the empty-window case — a not_authorized/error
  // state passes its OWN real message through untouched (falling back to WidgetFrame's own default
  // denial/error copy when the hook has none), never overridden by this tile's empty-state text.
  const displayMessage = overflow
    ? 'Window too large to summarize here — narrow the date range or filters.'
    : frameState === 'empty'
      ? (message ?? 'No activity in this period.')
      : message

  return (
    <WidgetFrame
      title="AI-accept rate"
      icon="activity"
      state={frameState}
      message={displayMessage}
      onRetry={retry}
    >
      {hasActivity ? (
        <KpiTile
          title="AI-accept rate"
          value={`${rate.ratePct}%`}
          valueCaption={`${rate.accepted} accepted · ${rate.rejected} rejected`}
          breakdown={
            rate.edited > 0
              ? [{ key: 'edited', label: 'Edited (excluded from the rate)', value: String(rate.edited) }]
              : []
          }
        />
      ) : null}
    </WidgetFrame>
  )
}
